import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Button,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import { getBenefitIcon, BenefitIconType } from '../../components/shared/BenefitIcons';
import { Link } from 'react-router-dom';
import useScrollToTop from '../../hooks/useScrollToTop';

const EB5: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  useScrollToTop();

  const benefits: { icon: BenefitIconType; text: string }[] = [
    { icon: 'work', text: t('us.eb5.benefits.noJobOffer') },
    { icon: 'check', text: t('us.eb5.benefits.noLaborCert') },
    { icon: 'family', text: t('us.eb5.benefits.familyGreenCard') },
    { icon: 'security', text: t('us.eb5.benefits.workAuth') },
    { icon: 'global', text: t('us.eb5.benefits.citizenshipPath') },
    { icon: 'banking', text: t('us.eb5.benefits.investmentOpps') },
  ];

  const requirements = [
    t('us.eb5.requirements.1'),
    t('us.eb5.requirements.2'),
    t('us.eb5.requirements.3'),
    t('us.eb5.requirements.4'),
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#ff9800',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '40vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('us.eb5.hero.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('us.eb5.hero.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Program Overview */}
      <Container sx={{ py: 8 }}>
        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h4" gutterBottom align="center">
                {t('us.eb5.overview.title')}
              </Typography>
              <Typography variant="body1" paragraph align="center">
                {t('us.eb5.overview.description1')}
              </Typography>
              <Typography variant="body1" paragraph align="center">
                {t('us.eb5.overview.description2')}
              </Typography>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {t('us.eb5.benefits.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ color: 'primary.main', mr: 2 }}>
                          {getBenefitIcon(benefit.icon)}
                        </Box>
                        <Typography variant="h6" component="h3">
                          {benefit.text}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Requirements Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          {t('us.eb5.requirements.title')}
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {t('us.eb5.requirements.investmentTitle')}
                  </Typography>
                  <List>
                    {requirements.map((requirement, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={requirement} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {t('us.eb5.timeline.title')}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary={t('us.eb5.timeline.initial.title')}
                        secondary={t('us.eb5.timeline.initial.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('us.eb5.timeline.conditional.title')}
                        secondary={t('us.eb5.timeline.conditional.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('us.eb5.timeline.permanent.title')}
                        secondary={t('us.eb5.timeline.permanent.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('us.eb5.timeline.citizenship.title')}
                        secondary={t('us.eb5.timeline.citizenship.description')}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Active Projects Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {t('us.eb5.activeProjects.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      {t('projects.twinlakes.title')}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {t('projects.twinlakes.description')}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        component={Link}
                        to="/projects/twinlakes"
                        variant="contained"
                        color="primary"
                      >
                        {t('projects.twinlakes.moreDetails')}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      {t('projects.hillcrest.title')}
                    </Typography>
                    <Typography variant="body1" paragraph>
                      {t('projects.hillcrest.description')}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        component={Link}
                        to="/projects/santacruz"
                        variant="contained"
                        color="primary"
                      >
                        {t('projects.hillcrest.moreDetails')}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default EB5; 